import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * Cross-app sign-in from tution-frontend (parent already logged in there).
 * Opens with hash: #token=...&d=BASE64URL(userData JSON)&mcp=0|1
 * Hash is not sent to the server; we persist to localStorage then replace URL.
 */
function decodeBase64UrlToString(s) {
  if (!s) return null;
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return null;
  }
}

/** Allow only in-app paths under /{parentPrefix}/ (no open redirects). */
function sanitizeSsoNextPath(raw, parentPrefix) {
  if (!raw || typeof raw !== "string") return null;
  let p = raw.trim();
  if (!p.startsWith("/")) p = `/${p}`;
  const esc = parentPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^/${esc}(/|$)`);
  if (!re.test(p)) return null;
  if (p.includes("//")) return null;
  const lower = p.toLowerCase();
  if (lower.includes("://") || lower.includes("@")) return null;
  return p;
}

const ParentSso = () => {
  const navigate = useNavigate();
  const parentPrefix = process.env.REACT_APP_PARENT_PREFIX || "parent";
  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) {
      navigate("/login", { replace: true });
      return;
    }

    let params;
    try {
      params = new URLSearchParams(hash);
    } catch {
      navigate("/login", { replace: true });
      return;
    }

    const token = params.get("token");
    if (!token || typeof token !== "string" || token.length < 10) {
      navigate("/login", { replace: true });
      return;
    }

    localStorage.setItem("token", token);
    localStorage.setItem("role", "parent");
    localStorage.removeItem("admin-token");
    // Drop RBAC cache from an older tution-admin session (different token/user).
    localStorage.removeItem("parent-permissions");
    localStorage.removeItem("tester-permissions");

    const d = params.get("d");
    if (d) {
      const json = decodeBase64UrlToString(d);
      if (json) {
        try {
          const parsed = JSON.parse(json);
          localStorage.setItem("userData", json);
          // Sidebar RBAC reads `parent-permissions`; must match login API (same origin as tution-admin).
          if (Array.isArray(parsed.permissions)) {
            localStorage.setItem(
              "parent-permissions",
              JSON.stringify(parsed.permissions)
            );
          } else {
            localStorage.removeItem("parent-permissions");
          }
        } catch {
          /* ignore invalid JSON */
        }
      }
    }

    const mcp = params.get("mcp");
    if (mcp === "1" || mcp === "0") {
      localStorage.setItem("mustChangePassword", mcp);
    }

    const nextRaw = params.get("next");
    const nextPath = sanitizeSsoNextPath(nextRaw, parentPrefix);
    const fallback = `/${parentPrefix}/`;
    window.location.replace(nextPath || fallback);
  }, [navigate, parentPrefix]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Signing you in…
      </Typography>
    </Box>
  );
};

export default ParentSso;
